import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  Image as ImageIcon,
  TextFields as TextIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import config from '../config';

const Messages = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [error, setError] = useState('');
  const [chatType, setChatType] = useState('all');
  const [chatId, setChatId] = useState('');
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [stats, setStats] = useState({ total_messages: 0, chat_id: null });
  const [activeTab, setActiveTab] = useState(0);

  // Test chat IDs for quick selection
  const sampleChats = [
    { id: '-1002475259063', name: 'Sample Group' },
    { id: '-4586738257', name: 'Another Chat' },
  ];

  const fetchMessages = async () => {
    if (!chatId.trim()) {
      toast.error('Please enter a Chat ID');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching messages for chat:', chatId, 'type:', chatType);
      
      // Convert chat_id to number if possible
      const chatIdNum = parseInt(chatId);
      const params = {
        chat_id: isNaN(chatIdNum) ? chatId : chatIdNum
      };
      
      const response = await axios.get(
        `${config.API_BASE}/chats/list-messages/${chatType}`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: params
        }
      );
      
      console.log('Messages API response:', response.data);
      
      if (response.data) {
        // Handle both response formats
        const messagesData = response.data.messages || response.data;
        const totalMessages = response.data.total_messages || 
                              (Array.isArray(messagesData) ? messagesData.length : 0);
        
        if (Array.isArray(messagesData)) {
          const formattedMessages = messagesData.map((msg, index) => ({
            id: msg.id || `msg_${index}`,
            message_id: msg.id || index + 1,
            chat_id: response.data.chat_id || chatId,
            text: msg.text || msg.message || '(No text)',
            date: msg.date ? new Date(msg.date).toLocaleString() : 
                  msg.timestamp ? new Date(msg.timestamp * 1000).toLocaleString() : 
                  'Unknown date',
            sender_id: msg.sender_id || msg.from_id || msg.sender || 'Unknown',
            sender_name: msg.sender_name || msg.from || msg.sender || 'Unknown',
            media: msg.media || false,
            reply_to: msg.reply_to || msg.reply_to_msg_id || null,
            is_media: msg.media ? 'Yes' : 'No',
            has_text: msg.text || msg.message ? 'Yes' : 'No',
          }));
          
          setMessages(formattedMessages);
          setStats({
            total_messages: totalMessages,
            chat_id: response.data.chat_id || chatId
          });
          
          toast.success(`Loaded ${formattedMessages.length} messages`);
        } else {
          setMessages([]);
          toast.info('No messages found in response');
        }
      } else {
        setMessages([]);
        toast.info('Empty response from server');
      }
    } catch (error) {
      console.error('Full error:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Failed to fetch messages';
      if (error.response) {
        if (error.response.status === 422) {
          errorMessage = 'Validation error: Check chat ID format';
        } else if (error.response.status === 404) {
          errorMessage = 'Chat not found or no messages';
        } else if (error.response.data?.detail) {
          errorMessage = `Error ${error.response.status}: ${error.response.data.detail}`;
        } else {
          errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Check if API is running.';
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!chatId.trim() || !messageText.trim()) {
      toast.error('Please enter Chat ID and message');
      return;
    }

    try {
      setSending(true);
      
      // Convert chat_id to number if possible
      const chatIdNum = parseInt(chatId);
      const payload = {
        chat_id: isNaN(chatIdNum) ? chatId : chatIdNum,
        message: messageText
      };
      
      console.log('Sending message with payload:', payload);
      
      await axios.post(`${config.API_BASE}/chats/message/send`, 
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Message sent successfully!');
      setMessageText('');
      // Refresh messages after sending
      setTimeout(() => fetchMessages(), 1000);
    } catch (error) {
      console.error('Send message error:', error.response || error);
      toast.error('Failed to send message: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSending(false);
    }
  };

  const handleExportExcel = () => {
    try {
      if (messages.length === 0) {
        toast.error('No messages to export');
        return;
      }

      const exportData = messages.map(msg => ({
        'Message ID': msg.message_id,
        'Chat ID': msg.chat_id,
        'Text': msg.text,
        'Date': msg.date,
        'Sender ID': msg.sender_id,
        'Sender Name': msg.sender_name,
        'Has Media': msg.is_media,
        'Has Text': msg.has_text,
        'Reply To': msg.reply_to,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Messages');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `telegram_messages_${stats.chat_id || 'chat'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Messages exported successfully!');
    } catch (error) {
      toast.error('Failed to export messages');
    }
  };

  const filteredMessages = messages.filter(msg =>
    (msg.text && msg.text.toLowerCase().includes(search.toLowerCase())) ||
    (msg.sender_name && msg.sender_name.toLowerCase().includes(search.toLowerCase())) ||
    (msg.sender_id && msg.sender_id.toString().includes(search))
  );

  const columns = [
    { field: 'message_id', headerName: 'Msg ID', width: 80 },
    { 
      field: 'text', 
      headerName: 'Message', 
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: params.value === '(No text)' ? 'text.disabled' : 'inherit',
          fontStyle: params.value === '(No text)' ? 'italic' : 'normal'
        }}>
          {params.row.is_media === 'Yes' ? '📷 Media' : params.value}
        </Typography>
      )
    },
    { 
      field: 'sender_name', 
      headerName: 'Sender', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      )
    },
    { field: 'date', headerName: 'Date', width: 180 },
    { 
      field: 'is_media', 
      headerName: 'Type', 
      width: 100,
      renderCell: (params) => (
        <Chip
          icon={params.value === 'Yes' ? <ImageIcon /> : <TextIcon />}
          label={params.value === 'Yes' ? 'Media' : 'Text'}
          size="small"
          sx={{
            bgcolor: params.value === 'Yes' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
            color: params.value === 'Yes' ? '#3b82f6' : '#10b981',
          }}
        />
      )
    },
  ];

  const chatTypes = [
    { value: 'all', label: 'All Messages' },
    { value: 'text', label: 'Text Only' },
    { value: 'media', label: 'Media Only' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Messages Management
        </Typography>
        <Chip 
          label="API Connected" 
          color="success" 
          variant="outlined"
          icon={<FilterIcon />}
        />
      </Box>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="View Messages" />
        <Tab label="Send Message" />
        <Tab label="Export Data" />
      </Tabs>

      {activeTab === 0 && (
        <Card sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              View Chat Messages
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Message Type</InputLabel>
                  <Select
                    value={chatType}
                    label="Message Type"
                    onChange={(e) => setChatType(e.target.value)}
                  >
                    {chatTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Chat ID"
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                  placeholder="e.g., -1002475259063"
                  helperText="Enter Telegram chat ID (usually negative for groups)"
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchMessages}
                  disabled={loading || !chatId}
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Load'}
                </Button>
              </Grid>
            </Grid>

            {/* Quick Chat IDs */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Quick select:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {sampleChats.map((chat) => (
                  <Chip
                    key={chat.id}
                    label={`${chat.name}: ${chat.id}`}
                    onClick={() => setChatId(chat.id)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Common issues:
                </Typography>
                <ul style={{ margin: '5px 0 0 20px' }}>
                  <li>Chat ID must be a valid Telegram chat ID</li>
                  <li>You need to be a member of the chat</li>
                  <li>Check if the chat exists</li>
                </ul>
              </Alert>
            )}

            {messages.length === 0 && !loading && chatId && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No Messages Found
                </Typography>
                <Typography color="textSecondary" sx={{ mb: 3 }}>
                  Try a different chat ID or check your access to this chat
                </Typography>
              </Box>
            )}

            {messages.length > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1">
                      Chat: <strong>{stats.chat_id}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total: {stats.total_messages} messages | Loaded: {messages.length}
                    </Typography>
                  </Box>
                  
                  <TextField
                    placeholder="Search messages..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ width: 250 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ height: 500, mb: 2 }}>
                  <DataGrid
                    rows={filteredMessages}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    checkboxSelection
                    onSelectionModelChange={(newSelection) => {
                      setSelectedMessages(newSelection);
                    }}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Showing {filteredMessages.length} of {messages.length} messages
                  </Typography>
                  
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleExportExcel}
                    variant="outlined"
                    size="small"
                  >
                    Export
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Send New Message
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Chat ID"
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                  placeholder="e.g., -1002475259063"
                  helperText="The Telegram chat ID to send message to"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message Text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message here..."
                  multiline
                  rows={4}
                  helperText="Supports Markdown formatting"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setMessageText('')}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSendMessage}
                    disabled={sending || !chatId || !messageText}
                    sx={{ minWidth: 150 }}
                  >
                    {sending ? <CircularProgress size={24} /> : 'Send Message'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> You must be a member of the chat and have permission to send messages.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Export Messages Data
            </Typography>
            
            {messages.length > 0 ? (
              <Box>
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  Ready to export {messages.length} messages from chat: {stats.chat_id}
                </Alert>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" sx={{ mb: 1 }}>
                        {messages.length}
                      </Typography>
                      <Typography color="textSecondary">
                        Messages Loaded
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="success" sx={{ mb: 1 }}>
                        Excel
                      </Typography>
                      <Typography color="textSecondary">
                        Export Format
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ mb: 1 }}>
                        {new Date().toLocaleDateString()}
                      </Typography>
                      <Typography color="textSecondary">
                        Export Date
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportExcel}
                    sx={{ px: 4 }}
                  >
                    Export to Excel
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      const dataStr = JSON.stringify(messages, null, 2);
                      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                      const link = document.createElement('a');
                      link.setAttribute('href', dataUri);
                      link.setAttribute('download', `messages_${stats.chat_id}.json`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success('JSON exported!');
                    }}
                  >
                    Export as JSON
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DownloadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No Messages to Export
                </Typography>
                <Typography color="textSecondary" sx={{ mb: 3 }}>
                  Load messages first using the "View Messages" tab
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setActiveTab(0)}
                >
                  Go to View Messages
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <Card sx={{ mt: 3, bgcolor: 'rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary">
              Debug Info: {config.API_BASE} | Token: {token ? 'Present' : 'Missing'}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Messages;